import { useMemo } from 'react';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import { isNumber } from 'utility/utility';
import { Meta, ProjectSelectTableRow, resolveRowId } from './ProjectSelectTable/helpers';
import {
    ProjectsListInfiniteParams as InfiniteParams,
    useBrandsQueriesByIds,
    useProjectsListInfinite,
    useTeamsQueriesByIds,
    useUsersQueriesByIds,
} from '@round/creatorbase-helpers/api-hooks';

type Params = Pick<InfiniteParams, 'search' | 'accessible_by_team_id'>;
type Config = { enabled?: boolean };

export function useProjectSelect(params: Params, config: Config = {}) {
    const projectsQuery = useProjectsListInfinite({ ...params, page_size: 25 }, config);

    const pages = useMemo(() => projectsQuery.data?.pages ?? [], [projectsQuery.data?.pages]);
    const projects = useMemo(() => pages.flatMap((p) => p.results), [pages]);

    const projectPageIndex = useMemo(() => {
        const map = new Map<number, number>();
        pages.forEach((page, i) => page.results.forEach((p) => map.set(p.id, i)));
        return map;
    }, [pages]);

    const brands = useBrandsQueriesByIds(
        pages.map((page) => uniq(page.results.map((p) => p.brand_id)).filter(isNumber)),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectSelectTableRow | {}) => {
                    const id = resolveRowId(row);
                    if (id === null) return true;
                    return queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false;
                },
            }),
        }
    );

    const teams = useTeamsQueriesByIds(
        pages.map((page) => uniq(page.results.map((p) => p.team_id)).filter(isNumber)),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectSelectTableRow | {}) => {
                    const id = resolveRowId(row);
                    if (id === null) return true;
                    return queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false;
                },
            }),
        }
    );

    const users = useUsersQueriesByIds(
        pages.map((page) => uniq(page.results.flatMap((p) => p.users_assigned_to_campaigns))),
        {
            combine: (queries) => ({
                data: uniqBy(
                    queries.flatMap((q) => q.data?.results ?? []),
                    'id'
                ),
                isLoadingFor: (row: ProjectSelectTableRow | {}) => {
                    const id = resolveRowId(row);
                    if (id === null) return true;
                    return queries[projectPageIndex.get(id) ?? -1]?.isLoading ?? false;
                },
            }),
        }
    );

    const rows: ProjectSelectTableRow[] = useMemo(
        () =>
            projects.map((project) => {
                const brand = brands.data.find((b) => b.id === project.brand_id) || null;
                const team = teams.data.find((t) => t.id === project.team_id) || null;
                const projectUsers = users.data.filter((u) => project.users_assigned_to_campaigns.includes(u.id));

                return {
                    ...project,
                    brand,
                    team,
                    users: projectUsers,
                };
            }),
        [projects, brands.data, teams.data, users.data]
    );

    const meta: Meta = {
        isLoading: projectsQuery.isLoading,
        isFetchingNextPage: projectsQuery.isFetchingNextPage,
        hasError: projectsQuery.status === 'error',
        getIsRowLoading: (row) => !('id' in row),
        isBrandLoading: brands.isLoadingFor,
        isTeamLoading: teams.isLoadingFor,
        isUsersLoading: users.isLoadingFor,
    };

    return {
        rows,
        meta,
        hasNextPage: projectsQuery.hasNextPage,
        fetchNextPage: projectsQuery.fetchNextPage,
    };
}
